import {
  type OasObjectFilterChild,
  type OasTableColumnFilter,
  isOasTableColumnFilterBoolean,
  isOasTableColumnFilterObject,
  isOasTableColumnFilterSelect,
} from '../../oas/OasTableColumnFilter';
import { isNonNullable } from '../../types/isNonNullable';
import { safeAbsurd } from '../../types/safeAbsurd';
import {
  type ObjectFilterChild,
  type TableFilter,
  createAsnFilter,
  createBooleanFilter,
  createIpFilter,
  createIpv6Filter,
  createLabelsFilter,
  createNumberFilter,
  createRoutingFilter,
  createRoutingIpv6Filter,
  createSelectFilter,
  createTextFilter,
  createUptimeFilter,
} from '../filters/index';
import { createDateTimeFilter } from '../filters/types/dateTimeFilter';
import { createObjectFilter } from '../filters/types/objectFilter';

const stripInvalidObjectChild = (filter: TableFilter): TableFilter => ({
  ...filter,
  // cannot do without any since filter.operators is an union of string array unions
  // and typescript does not see it can just filter them as strings

  operators: (filter.operators as any[]).filter(
    (op: TableFilter['operators'][number]) => op !== 'color',
  ),
});

export const createObjectFilterChildFromOas = (
  oasChild: OasObjectFilterChild,
): ObjectFilterChild | undefined => {
  const filter = createTableColumnFilterFromOas(oasChild.filter);
  return filter
    ? {
        ...oasChild,
        filter: stripInvalidObjectChild(filter),
      }
    : undefined;
};

export const createTableColumnFilterFromOas = (
  filter: OasTableColumnFilter,
): TableFilter | undefined => {
  if (isOasTableColumnFilterObject(filter)) {
    return createObjectFilter(
      filter.children.map(createObjectFilterChildFromOas).filter(isNonNullable),
    );
  }
  if (isOasTableColumnFilterSelect(filter)) {
    return createSelectFilter(filter.options, filter.multi);
  }
  if (isOasTableColumnFilterBoolean(filter) || filter === 'boolean') {
    return createBooleanFilter(
      isOasTableColumnFilterBoolean(filter) ? filter.options : undefined,
    );
  }

  switch (filter) {
    case 'asn':
      return createAsnFilter();
    case 'ip':
      return createIpFilter();
    case 'number':
      return createNumberFilter();
    case 'routing':
      return createRoutingFilter();
    case 'text':
      return createTextFilter();
    case 'uptime':
      return createUptimeFilter();
    case 'routingIpv6':
      return createRoutingIpv6Filter();
    case 'ipv6':
      return createIpv6Filter();
    case 'dateTime':
      return createDateTimeFilter();
    case 'labels':
      return createLabelsFilter();
    default:
      safeAbsurd(filter);
      return undefined;
  }
};
